<!-- 陪练分析报告 -->
<template>
  <div
    v-loading="pageLoading"
    class="practice-report"
    :class="hasPending ? 'status-pending' : 'status-normal'"
  >
    <!-- score -->
    <div class="practice-report__score">
      <div v-if="hasPending" class="item-status item-status--analyzing">
        <div class="item-status__tag">分析中</div>
      </div>
      <div v-else class="item-status">
        <span
          class="item-status__score"
          :style="!dataInfo.practiceRecordPojo?.score && 'color: #F54E4E'"
          >{{ dataInfo.practiceRecordPojo.score || 0 }}</span
        >
        <span class="item-status__normal">/100</span>
      </div>
      <div class="item-info">
        <span class="item-info__name">
          {{ dataInfo.practiceRecordPojo.taskName }}
        </span>
        <p class="item-info__time">
          <span>
            <i class="icon-calendar-schedule-line t-iconfont"></i>
            {{
              xbb.formatTime(dataInfo.practiceRecordPojo.completedTime * 1000, 'yyyy-MM-dd HH:mm')
            }}
          </span>
          <span class="item-info__cutline"></span>
          <span>
            <i class="icon-time-line t-iconfont"></i>
            {{ dataInfo.practiceRecordPojo.duration }}
          </span>
        </p>
      </div>
    </div>
    <!-- has pending -->
    <template v-if="hasPending">
      <div class="practice-report__pending">
        <PracticeEmptyPending />
      </div>
    </template>
    <!-- has data -->
    <template v-else>
      <!-- cut-line -->
      <div class="cut-line"></div>
      <!-- section-tabs -->
      <div class="section-tabs">
        <button
          class="section-tabs__item"
          :class="{ 'section-tabs__item--active': activeTab === 'analysis' }"
          type="button"
          @click="activeTab = 'analysis'"
        >
          亮点与不足
        </button>
        <button
          class="section-tabs__item"
          :class="{ 'section-tabs__item--active': activeTab === 'session' }"
          type="button"
          @click="activeTab = 'session'"
        >
          对话原文
        </button>
      </div>
      <template v-if="activeTab === 'analysis'">
        <!-- record analysis -->
        <div v-if="dataInfo.messageList?.length" class="record-analysis">
          <div v-if="dataInfo.practiceResult?.good?.length" class="record-analysis__good">
            <AnalysisCard
              v-for="(good, index) in dataInfo.practiceResult.good"
              :key="index"
              :data-info="{
                ...good,
                status: 'good'
              }"
              :message-list="dataInfo.messageList"
              @view-quote="handleViewQuote"
            />
          </div>
          <div v-if="dataInfo.practiceResult?.bad?.length" class="record-analysis__bad">
            <AnalysisCard
              v-for="(bad, index) in dataInfo.practiceResult.bad"
              :key="index"
              :data-info="{
                ...bad,
                status: 'bad'
              }"
              :message-list="dataInfo.messageList"
              @view-quote="handleViewQuote"
            />
          </div>
        </div>
        <div v-else class="analysis-empty">
          <img class="analysis-empty__img" :src="empty" />
          <div class="analysis-empty__desc">
            本次练习未检测到有效发言，暂不产生评分（记为0分）放轻松，整理下思路，下次大胆开口试一试吧！
          </div>
        </div>
      </template>
      <template v-else>
        <!-- session record text -->
        <div v-if="dataInfo.messageList?.length" class="record-session">
          <SessionCard
            ref="sessionCardRef"
            :bad-list="dataInfo.practiceResult?.bad || []"
            :data-info="dataInfo"
            :data-list="dataInfo.messageList"
          />
        </div>

        <div v-else class="analysis-empty">
          <img class="analysis-empty__img" :src="empty" />
          <div class="analysis-empty__desc">本次练习未检测到有效发言</div>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup>
import empty from '@/assets/empty-bg.png'
import {
  defineOptions,
  onMounted,
  onUnmounted,
  ref,
  defineProps,
  nextTick,
  defineExpose,
  watch
} from 'vue'

defineOptions({
  name: 'PracticeReport'
})
// api
import { getPracticeRecordDetail } from '@/api/ai-board.js'
// utils

import xbb from '@xbb/xbb-utils'
// props
const props = defineProps({
  params: {
    type: Object,
    default: () => ({})
  }
})
const emit = defineEmits(['ready'])
// components
import AnalysisCard from './analysis-card.vue'
import SessionCard from './session-card.vue'
import PracticeEmptyPending from './practice-empty-pending.vue'
// data
const getDefaultDataInfo = () => ({
  messageList: [],
  practiceResult: {
    bad: [],
    good: []
  },
  practiceRecordPojo: {
    duration: '',
    completedTime: 0,
    taskName: ''
  },
  userName: ''
})

const pageLoading = ref(true)
const activeTab = ref('analysis')
const sessionCardRef = ref(null)
const dataInfo = ref(getDefaultDataInfo())
// 是否展示分析中
const hasPending = ref(true)
const pollTimer = ref(null)
const requestLoading = ref(false)
let latestRequestToken = 0

const normalizePracticeResult = (practiceResult = {}) => {
  return {
    good: Array.isArray(practiceResult.good) ? practiceResult.good : [],
    bad: Array.isArray(practiceResult.bad) ? practiceResult.bad : []
  }
}

const hasPracticeScore = (practiceRecordPojo = {}) => {
  return practiceRecordPojo.score !== null && practiceRecordPojo.score !== undefined
}

const normalizeDataInfo = (result = {}) => {
  return {
    ...getDefaultDataInfo(),
    ...result,
    practiceResult: normalizePracticeResult(result?.practiceResult),
    messageList: Array.isArray(result?.messageList) ? result.messageList : [],
    practiceRecordPojo: {
      ...getDefaultDataInfo().practiceRecordPojo,
      ...(result?.practiceRecordPojo || {})
    }
  }
}

const stopPolling = () => {
  if (!pollTimer.value) {
    return
  }

  clearInterval(pollTimer.value)
  pollTimer.value = null
}

const startPolling = () => {
  if (pollTimer.value) {
    return
  }

  pollTimer.value = setInterval(() => {
    getDataDetail({ silent: true })
  }, 10000)
}

const syncPendingStatus = (practiceRecordPojo = {}) => {
  const wasPending = hasPending.value
  const nextPending = !hasPracticeScore(practiceRecordPojo)
  hasPending.value = nextPending

  if (nextPending) {
    startPolling()
    return
  }

  stopPolling()
  if (wasPending) {
    emit('ready', {
      practiceRecordId: props.params?.practiceRecordId,
      practiceRecordPojo
    })
  }
}

const resetDataDetail = () => {
  stopPolling()
  pageLoading.value = true
  activeTab.value = 'analysis'
  dataInfo.value = getDefaultDataInfo()
  hasPending.value = false
}

// computed
// methods
const getDataDetail = ({ silent = false } = {}) => {
  if (!props.params?.practiceRecordId) {
    return
  }

  if (requestLoading.value && silent) {
    return
  }

  if (!silent) {
    pageLoading.value = true
  }

  const requestToken = ++latestRequestToken
  requestLoading.value = true

  getPracticeRecordDetail({
    ...props.params
  })
    .then((res) => {
      if (requestToken !== latestRequestToken) {
        return
      }

      dataInfo.value = normalizeDataInfo(res.result)
      syncPendingStatus(dataInfo.value.practiceRecordPojo)
    })
    .finally(() => {
      if (requestToken !== latestRequestToken) {
        return
      }

      requestLoading.value = false
      nextTick(() => {
        pageLoading.value = false
      })
    })
}
// 切换当前状态
const changePendingStatus = () => {
  hasPending.value = true
  startPolling()
}

const handleViewQuote = (messageIndex) => {
  activeTab.value = 'session'
  nextTick(() => {
    sessionCardRef.value?.scrollToMessage?.(messageIndex)
  })
}
// lice-cycle

watch(
  () => props.params?.practiceRecordId,
  (practiceRecordId, prevPracticeRecordId) => {
    if (!practiceRecordId || practiceRecordId === prevPracticeRecordId) {
      return
    }

    resetDataDetail()
    getDataDetail()
  }
)

onMounted(() => {
  getDataDetail()
})

onUnmounted(() => {
  stopPolling()
})

// export
defineExpose({
  changePendingStatus
})
</script>

<style lang="less" scoped>
.practice-report {
  position: relative;
  width: 100%;
  height: 100%;
  padding: 0 12px 12px;

  &.status-pending {
    display: flex;
    flex-direction: column;
  }

  &.status-normal {
    overflow-y: auto;
  }

  .cut-line {
    margin: 16px 0;
    // border-top: 1px dashed #f2f3f5;
    height: 1px;
    background: repeating-linear-gradient(
      to right,
      #f2f3f5,
      #f2f3f5 4px,
      transparent 4px,
      transparent 8px
    );
  }

  .title-line {
    line-height: 26px;
    color: #323233;
    font-size: 18px;
    font-weight: 600;
  }

  .section-tabs {
    display: flex;
    align-items: center;
    gap: 12px;

    &__item {
      min-width: 68px;
      height: 24px;
      padding: 4px 10px;
      border: none;
      border-radius: 8px;
      background: #f2f3f5;
      color: #323233;
      font-size: 12px;
      line-height: 16px;
      font-family: inherit;
      cursor: pointer;

      &--active {
        background: #7a52f4;
        color: #fff;
      }
    }
  }

  &__score {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    background: linear-gradient(283deg, #eef2ff 1%, #f5f3ff 100%);
    border-radius: 16px;
    border: 1px solid rgba(124, 58, 237, 0.15);

    .item-status {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 60px;
      height: 60px;
      border-radius: 12px;
      background: #fff;

      &__score {
        line-height: 34px;
        font-weight: 600;
        font-size: 24px;
        color: #7a52f4;
      }

      &__normal {
        line-height: 16px;
        font-size: 12px;
        color: #969799;
      }

      &__tag {
        padding: 2px 8px;
        border-radius: 16px;
        background: #f2f3f5;
        line-height: 16px;
        font-size: 12px;
        color: #646566;
        white-space: nowrap;
      }
    }

    .item-status--analyzing {
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.04);
    }

    .item-info {
      margin-left: 12px;
      display: flex;
      flex-direction: column;

      &__name {
        font-size: 16px;
        font-weight: 600;
        line-height: 22px;
        color: #323233;

        .t-iconfont {
          color: #969799;
        }
      }

      &__cutline {
        width: 1px;
        height: 12px;
        background-color: #e5e6eb;
      }

      &__time {
        height: 16px;
        display: flex;
        align-items: center;
        margin-top: 4px;
        font-size: 12px;
        gap: 12px;
        color: #323233;

        .t-iconfont {
          margin-right: 2px;
          font-size: 12px;
          width: 12px;
          height: 12px;
          color: #969799;
        }
      }
    }
  }

  &__pending {
    flex-grow: 1;
    margin-top: 16px;
    min-height: 0;
  }

  .record-analysis {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 16px;

    &__good,
    &__bad {
      box-sizing: border-box;
      padding: 12px;
      display: flex;
      flex-direction: column;
      gap: 0;
      border-radius: 16px;
      border-left: 2px solid transparent;

      > * + * {
        position: relative;
        margin-top: 12px;
        padding-top: 12px;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: repeating-linear-gradient(
            to right,
            #e5e6eb,
            #e5e6eb 4px,
            transparent 4px,
            transparent 8px
          );
        }
      }
    }

    &__good {
      background: #f7f8fa;
      border-left-color: #00c241;
    }

    &__bad {
      background: #f7f8fa;
      border-left-color: #f54e4e;
    }
  }

  .record-session {
    margin-top: 16px;
  }

  .analysis-empty {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    &__img {
      margin-top: 12px;
    }
    &__desc {
      color: #969799;
      font-size: 14px;
      line-height: 20px;
      text-align: center;
    }
  }
}
</style>
